import { z } from 'zod';
import { GUIDE_VERSION_MAX, GUIDE_VERSION_MIN } from './visualGuide.js';
import { AdviserRole, AssetType, Frequency, HoldingOwner, IncomeKind, IncomeStreamType, LiabilityKind, LiabilityType, LifeEventDirection, LifeEventKind, PlanTier, ScenarioStatus, SpendingCategory, SpendingKind, Wrapper, } from './domain.js';
/**
 * Zod schemas mirroring the wire contracts. Runtime validation lives here so
 * both the API and the web client share a single source of truth. The wire
 * shape is JSON, so amounts are validated as an integer `number` of pence (the
 * substantive hard-rule #5 guarantee — always a whole number of pence); the
 * branded `Pence` type is applied at the domain boundary (the engine and the
 * API route import `Pence` from `@cf/shared` and brand the validated input).
 */
// --- primitives -----------------------------------------------------------
/** Integer pence — always a whole number. The wire type is a plain integer. */
export const penceSchema = z
    .number()
    .int('Amounts must be an integer number of pence')
    .describe('Integer pence');
/** Integer pence that may not be negative (e.g. an income/expense magnitude). */
export const nonNegativePenceSchema = penceSchema.min(0, 'Amount cannot be negative');
/**
 * An ISO calendar date (YYYY-MM-DD). Validated as a REAL calendar date in a
 * plausible planning range (1900–2200) — a partially typed year from a date
 * input (e.g. "0007-01-01" en route to "1970-…") must fail closed here at the
 * contract, not crash later inside the engine's date handling.
 *
 * The two failure MESSAGES are written for the person typing, not for the
 * person who wrote the schema (IFA review 2026-08-23, aspect 3 #4). "Must be
 * an ISO date (YYYY-MM-DD)" appeared under a date input that renders
 * dd/mm/yyyy, so the app described the value in a format it was not showing.
 * The wire format is unchanged — YYYY-MM-DD is still the only thing that
 * parses; only the sentence a user reads is different, and it is fixed HERE
 * rather than per-dialog so every date field in the product says the same
 * thing.
 */
const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter the date as dd/mm/yyyy')
    .refine((s) => {
    const year = Number(s.slice(0, 4));
    const month = Number(s.slice(5, 7));
    const day = Number(s.slice(8, 10));
    if (year < 1900 || year > 2200)
        return false;
    if (month < 1 || month > 12 || day < 1)
        return false;
    const max = month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];
    return max !== undefined && day <= max;
}, 'Enter a real date as dd/mm/yyyy — the year must be between 1900 and 2200');
/** An ISO instant (timestamptz) as returned by the DB on read. */
export const isoInstantSchema = z.string().datetime({ offset: true });
export const firmIdSchema = z.string().uuid();
export const adviserIdSchema = z.string().uuid();
export const clientIdSchema = z.string().uuid();
export const frequencySchema = z.nativeEnum(Frequency);
export const adviserRoleSchema = z.nativeEnum(AdviserRole);
export const planTierSchema = z.nativeEnum(PlanTier);
export const wrapperSchema = z.nativeEnum(Wrapper);
export const incomeKindSchema = z.nativeEnum(IncomeKind);
export const spendingKindSchema = z.nativeEnum(SpendingKind);
export const liabilityKindSchema = z.nativeEnum(LiabilityKind);
export const lifeEventKindSchema = z.nativeEnum(LifeEventKind);
export const lifeEventDirectionSchema = z.nativeEnum(LifeEventDirection);
/** An integer number of basis points (1 bp = 0.01%). Rates are NEVER floats. */
export const bpsSchema = z.number().int('Rates must be an integer number of basis points');
/**
 * Taxpayer region. `rUK` is the only valid domain value (see `TaxRegion`), but
 * the write path still recognises the STRING `'scotland'` for one reason: so a
 * Scottish taxpayer arriving from an import, an older payload or an integration
 * is refused with an explanation instead of a generic "invalid enum value".
 *
 * Recognising it in order to refuse it is not the same as supporting it — it is
 * what keeps the boundary explicit rather than silent. Do not "simplify" this to
 * `z.literal('rUK')`: that trades a clear refusal for an opaque one.
 */
export const taxRegionSchema = z.enum(['rUK', 'scotland']).refine((r) => r === 'rUK', {
    // Client-visible at field level — plain scope wording, no internal
    // "Phase 1" vocabulary (2026-08-26 quality review).
    message: 'Scottish taxpayers are outside this tool’s current scope (hard-gated). Only rest-of-UK (rUK) is supported.',
});
// --- envelope + errors ----------------------------------------------------
export const apiErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    field: z.string().optional(),
});
export const fieldErrorSchema = z.object({
    field: z.string(),
    code: z.string(),
    message: z.string(),
});
export const envelopeMetaSchema = z.object({
    requestId: z.string(),
    version: z.string(),
    timestamp: z.string().optional(),
});
export function envelopeSchema(data) {
    return z.object({
        data: data.nullable(),
        meta: envelopeMetaSchema,
        errors: z.array(apiErrorSchema),
    });
}
export const healthDataSchema = z.object({
    status: z.literal('ok'),
    service: z.string(),
    /**
     * The deployed code identity (git SHA set by the deploy pipeline as
     * CODE_VERSION; 'dev' outside CI/CD). Exposed on the PUBLIC health endpoint so
     * a plain `curl /api/v1/health` proves WHICH build a running service is on —
     * the check for "is prod actually running this merge, or has it drifted?".
     * Non-secret by construction (a commit SHA).
     */
    codeVersion: z.string(),
    /**
     * Extraction-engine capability (docs/integration/engine-import.md).
     *
     * `null` means no engine is configured, so Import notes is not offered at
     * all (the built-in parser this comment used to name was deleted in #99).
     * When present, `matchesPin` is the safeguard-4f
     * verdict: the engine is only OFFERED to the browser when its reported
     * identity equals the versions this build pins. A mismatched engine is
     * advertised as unusable rather than silently used — the capability and the
     * extract route's refusal agree, so the panel never offers a parser the API
     * would reject.
     *
     * Non-secret by construction: two version strings and a boolean. The engine's
     * ADDRESS is deliberately absent — the browser must never learn it.
     */
    engine: z
        .object({
        status: z.string(),
        modelVersion: z.string(),
        draftParserVersion: z.string(),
        /** The draft contract fork the engine emits (remediation 2026-09-17). Optional: an older image does not report it. */
        contractVersion: z.string().optional(),
        matchesPin: z.boolean(),
        /**
         * The commit the engine image was built from — the engine's counterpart
         * to `codeVersion` above, and for the same reason: it proves WHICH build
         * is running. `matchesPin` cannot, because a rule change moves neither
         * version string. Optional: an older image does not report it.
         *
         * Non-secret by construction (a commit sha), exactly as codeVersion is.
         */
        gitCommit: z.string().optional(),
    })
        .nullable()
        .optional(),
    /**
     * THE SCHEMA THE RUNNING CODE IS SITTING ON (B2.43, raised 2026-09-07, hit in
     * production 2026-09-09).
     *
     * `.github/workflows/deploy.yml` rolls a container and verifies its
     * `codeVersion`; nothing anywhere verified the DATABASE. So a merge carrying
     * four migrations' worth of new tables deployed green, and every route that
     * queried one 500'd — the fact-find save first, which meant no adviser could
     * save a plan at all. The symptom the owner actually reported was a review
     * checklist whose boxes would not tick.
     *
     * `codeVersion` answers "is production running this merge". This answers the
     * other half — "is production's schema the one this merge was written
     * against" — and the deploy's verify job fails on it. Two version facts, one
     * endpoint, both asserted from outside.
     *
     * `pending` is the number of migrations on disk that the database has not
     * applied. `null` means the API could not tell (no admin credential
     * configured) — reported as unknown rather than as zero, because "I did not
     * look" and "there is nothing to apply" are the two answers a deploy gate
     * must never confuse.
     *
     * COUNTS ONLY, and the names deliberately withheld. The first cut published
     * `pendingIds`, and the review was right that the set is more than the sum of
     * its parts: this repo's migrations are named `0011_policy_hardening_denial_
     * matrix`, `0010_soft_delete_grant_hardening`. A non-zero `pending` already
     * tells an anonymous caller that this instance is in a known-degraded state;
     * naming the files would tell them WHICH control has not landed, on an
     * unauthenticated endpoint, at the moment it is missing. The gate never
     * needed them — `.github/workflows/deploy.yml` fails on the count — and the
     * ids are already in CloudWatch under `SCHEMA_BEHIND_CODE`, which is where
     * whoever fixes it is looking anyway.
     */
    schema: z
        .object({
        pending: z.number().int().min(0),
        /** How many this boot applied (MIGRATE_ON_BOOT). */
        appliedOnBoot: z.number().int().min(0),
    })
        .nullable()
        .optional(),
});
// --- Client contract ------------------------------------------------------
/**
 * What an adviser submits to create a client. `firmId` is NOT accepted here —
 * it comes from the authenticated session and RLS context, never the body.
 * `.strict()` rejects unknown keys so a stray field can't slip past validation.
 */
export const clientCreateSchema = z
    .object({
    displayName: z.string().trim().min(1, 'Client name is required').max(200),
    dateOfBirth: isoDateSchema,
    taxRegion: taxRegionSchema.default('rUK'),
})
    .strict();
/** A client as returned on read. */
export const clientReadSchema = z.object({
    id: clientIdSchema,
    firmId: firmIdSchema,
    displayName: z.string(),
    dateOfBirth: isoDateSchema,
    status: z.enum(['active', 'archived']),
    taxRegion: z.literal('rUK'),
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
    deletedAt: isoInstantSchema.nullable(),
    /**
     * Workspace v2 §14 (0048) — this adviser's practice example, identified by the
     * data source and never by its name. DEFAULTED, not required: this schema is
     * embedded in stored report snapshots and client exports, and every one
     * written before the field existed must still parse (as not an example).
     */
    isExample: z.boolean().default(false),
});
/** Open (create or resume) the caller's own practice example — no client id: it is the session's. */
export const exampleClientOpenSchema = z
    .object({
    asOfDate: isoDateSchema,
    startYear: z.number().int().min(2000).max(2100),
})
    .strict()
    .refine((v) => Number(v.asOfDate.slice(0, 4)) === v.startYear, {
    path: ['startYear'],
    message: 'The start year must be the year of the as-of date',
});
export const exampleClientOpenResponseSchema = z.object({
    client: clientReadSchema,
    /** False when an existing example was resumed rather than created. */
    created: z.boolean(),
    factFindVersion: z.number().int().positive(),
});
/**
 * A client AS IT APPEARS IN THE LIST — the read shape plus three derived
 * workflow timestamps that say how far the modelling for that client has got.
 *
 * Deliberately a SEPARATE schema rather than three more fields on
 * `clientReadSchema`. That schema is embedded verbatim inside stored report
 * snapshots (`client: clientReadSchema`), and a snapshot is evidence: adding
 * required fields to it would mean every snapshot written before today failed
 * to re-parse. The list is a view; the record is a record.
 *
 * All three are NULLABLE and all three are PAST-TENSE FACTS with a record
 * behind them — "the adviser saved a fact-find at 14:32", never a judgement
 * about the client's plan. Nothing here may grow into "on track", "needs
 * attention" or "ready for a report": those are opinions about a projection,
 * and this product is a modelling aid, not advice. The list screen has no
 * assumptions panel next to it to justify a figure, which is the other reason
 * no monetary value belongs in this shape.
 *
 * Provenance of each (see the list route for the joins):
 *  - factFindSavedAt    — `household_fact_finds.updated_at` (one live row/client)
 *  - projectionSavedAt  — latest `household_projection_runs.created_at`
 *  - reportExportedAt   — latest RENDERED `report_exports`, reached ONLY through
 *    `household_projection_run_id`. `report_exports` is firm-scoped in RLS, so
 *    joining it directly would report another adviser's client activity.
 */
export const clientListItemSchema = clientReadSchema.extend({
    factFindSavedAt: isoInstantSchema.nullable(),
    projectionSavedAt: isoInstantSchema.nullable(),
    reportExportedAt: isoInstantSchema.nullable(),
    /**
     * Which adviser holds this client — the live `client_access` row at
     * `access_level = 'owner'`.
     *
     * NULL means "Not assigned", which is a real state: the holder's access row
     * was retired without a replacement. It must render as those words, never as
     * a blank cell, or an unassigned client is invisible in a firm that needs to
     * claim it.
     *
     * Carrying the NAME here is a deliberate disclosure, decided by the firm
     * principal (2026-08-26): colleagues may see each other's names, but not each
     * other's clients. The second half is what `clients` RLS already enforces —
     * a non-owner still only sees the clients they hold — so this field tells an
     * adviser WHO holds a client they can already open, and tells the principal
     * who holds everything.
     */
    adviserId: adviserIdSchema.nullable(),
    adviserName: z.string().nullable(),
    /** Whether that adviser is still active in the firm (a departure shows). */
    adviserActive: z.boolean(),
    /**
     * Where the REVIEW stands (GOAL 2026-09-10 item 11 — the review queue).
     * Three facts about the file, never a verdict on the plan:
     *   `none`      — no review has ever been completed and none declared ready;
     *   `completed` — the current review is declared ready to present (locked);
     *   `draft`     — a review is in progress: either the first one, or one
     *                 started from a completed review (`draftStartedAt`).
     */
    reviewState: z.enum(['none', 'draft', 'completed']).default('none'),
    /** When the most recent review was declared ready; null if never. */
    lastReviewLockedAt: isoInstantSchema.nullable().default(null),
    /** When the current draft was started from the previous completed review; null otherwise. */
    draftStartedAt: isoInstantSchema.nullable().default(null),
});
/**
 * The firm's default review cadence, in months. Twelve is the ordinary annual
 * review of an ongoing-service agreement; it is a WORKFLOW fact used to say
 * "next review due by <date>" on the client list, not a judgement about the
 * plan. A per-firm setting is the intended home for it (see TODO.md
 * GOAL 2026-09-10 item 11); until the owner decides, this is the one figure.
 */
export const REVIEW_INTERVAL_MONTHS = 12;
/**
 * Reassign a client to another adviser. OWNER-ONLY, by the firm principal's
 * decision — enforced in the route AND by migration 0033's policies, so the
 * database refuses it even if a future route forgets to.
 *
 * `.strict()`: the body names the incoming adviser and nothing else. It cannot
 * carry a firm, a client, or an access level — those come from the session and
 * the URL, never the caller.
 */
export const clientAdviserReassignSchema = z
    .object({
    adviserId: adviserIdSchema,
})
    .strict();
/** The list response: an array of the view shape above. */
export const clientListSchema = z.array(clientListItemSchema);
/**
 * Archive / restore a client — the ONLY field a status PATCH may carry. Archive
 * is a lifecycle flip (`status`), distinct from soft-delete (`deleted_at`);
 * `.strict()` keeps the body from smuggling any other column.
 */
export const clientStatusUpdateSchema = z
    .object({
    status: z.enum(['active', 'archived']),
})
    .strict();
// --- Firm & adviser contracts ----------------------------------------------
/**
 * A firm's brand colour: a 6-digit hex, LOWER-CASED before it is validated.
 *
 * The case fold is the whole point of having a named schema rather than an
 * inline regex. Migration 0034's CHECK admits lowercase only, so the contract
 * and the column agree on ONE stored form: '#7A1F3D' and '#7a1f3d' are the
 * same colour, and a case-sensitive comparison downstream would disagree with
 * the eye. A firm pasting an uppercase hex out of a brand guideline is doing
 * nothing wrong, so this normalises rather than refuses.
 *
 * Presentation only. A colour never enters an `AssumptionSpec`, an
 * `assumptions_snapshot` or a stored projection run — it cannot move a figure,
 * so changing it can never restate a projection already shown to a client.
 */
export const firmBrandColourHexSchema = z
    .string()
    .transform((s) => s.trim().toLowerCase())
    .pipe(z.string().regex(/^#[0-9a-f]{6}$/, 'Must be a 6-digit hex colour, e.g. #1a2b3c'));
/**
 * The colour the app wears when a firm has not chosen one.
 *
 * This is `--colour-teal-600`, the value `theme-firm.css` has always used for
 * `--accent` — NOT the '#1f2937' slate `profile.ts` used to return. Those two
 * disagreed harmlessly only because the brand accent was masked in the CSS
 * cascade and never painted anything. Unmasking it without aligning this
 * constant would have turned every firm's app from teal to grey on deploy: a
 * visible, unrequested rebrand of every existing customer, dressed up as a
 * white-label feature.
 */
export const PRODUCT_BRAND_COLOUR_HEX = '#1f716c';
/**
 * A firm as returned on read (branding drives the app chrome, report and PDF
 * theming). `brandColourHex` here is RESOLVED, never null: the API substitutes
 * the product colour when the firm has not set one, so every consumer gets a
 * paintable value and none has to carry its own fallback. The stored,
 * nullable value is on `firmBrandingSchema` below.
 */
export const firmSchema = z
    .object({
    id: firmIdSchema,
    name: z.string().trim().min(1, 'Firm name is required').max(200),
    logoUrl: z.string().url().nullable(),
    brandColourHex: firmBrandColourHexSchema,
    tier: planTierSchema,
})
    .strict();
/**
 * Firm-logo upload request. The image arrives as a base64 payload (the browser
 * reads the chosen file and encodes it). The base64 is length-capped so an
 * oversized body is refused at the contract before decoding; the decoded bytes
 * are then magic-byte-validated server-side (PNG/JPEG/WebP only, SVG rejected).
 * 512 KB of raw bytes is ~700 KB base64 — the cap is set above that with margin.
 */
export const firmLogoUploadRequestSchema = z
    .object({
    contentBase64: z
        .string()
        .min(1, 'A logo image is required')
        .max(1_000_000, 'Logo is too large; use an image of 512 KB or less')
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Logo must be base64-encoded image data'),
})
    .strict();
/**
 * Firm branding as returned on read/after upload: the logo as a short-lived
 * pre-signed URL (null when the firm has no logo). Never a raw S3 key.
 *
 * `brandColourHex` is the STORED value, so it is nullable — null means "use
 * the product colour", the same "unset, explicitly" idiom the firm's
 * assumption sets use. That is deliberately different from `firmSchema`,
 * where the field is already resolved: this payload is what the owner is
 * editing, and "I have not chosen one" has to be expressible.
 */
export const firmBrandingSchema = z
    .object({
    logoUrl: z.string().url().nullable(),
    brandColourHex: firmBrandColourHexSchema.nullable(),
})
    .strict();
/**
 * PUT body for the firm's brand colour. Its own object rather than a reuse of
 * `firmBrandingSchema` because the logo is not settable this way — it arrives
 * as bytes on its own multipart-ish route — and a body that appeared to accept
 * `logoUrl` would invite a caller to send a URL we would silently ignore.
 *
 * Explicitly nullable: sending null is how an owner REVERTS to the product
 * colour. That has to be a value the contract admits, or reverting becomes
 * impossible without a second endpoint.
 */
export const firmBrandColourUpdateSchema = z
    .object({
    brandColourHex: firmBrandColourHexSchema.nullable(),
})
    .strict();
/**
 * Request to generate a branded PDF from a stored household projection run.
 * The adviser must ATTEST — before generation — that they have reviewed the
 * assumptions and understand the output is illustrative, not advice. The
 * attestation is a hard gate: `confirmed` must be exactly `true`, so an
 * un-ticked box is a 422, never a silently-unattested report.
 */
export const reportExportRequestSchema = z
    .object({
    householdProjectionRunId: z.string().uuid(),
    attestation: z
        .object({
        confirmed: z.literal(true, {
            errorMap: () => ({
                message: 'You must confirm you have reviewed the assumptions and that this is illustrative, not advice.',
            }),
        }),
    })
        .strict(),
    /** Optional: which years to include in the calculation appendix. */
    appendixYears: z.array(z.number().int().min(1900).max(2200)).max(10).optional(),
    /**
     * Include the client's SAVED Cash Flow Modeller scenarios, each re-run server-side
     * from its stored definition, as extra sections in the PDF. Defaults to true
     * (the comprehensive report); set false for a base-plan-only export.
     */
    includeSavedScenarios: z.boolean().default(true),
})
    .strict();
export const reportExportStatusSchema = z.enum(['queued', 'rendering', 'rendered', 'failed']);
/**
 * A report export as returned on create/read. `downloadUrl` is a short-lived
 * pre-signed GET (null until rendered or on failure); never a raw S3 key.
 */
export const reportExportReadSchema = z
    .object({
    id: z.string().uuid(),
    status: reportExportStatusSchema,
    downloadUrl: z.string().url().nullable(),
    sha256: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    pageCount: z.number().int().positive().nullable(),
    createdAt: z.string(),
})
    .strict();
/** An adviser's profile within a firm, as returned on read. */
export const adviserProfileSchema = z
    .object({
    adviserId: adviserIdSchema,
    firmId: firmIdSchema,
    displayName: z.string().trim().min(1, 'Display name is required').max(200),
    email: z.string().email(),
    role: adviserRoleSchema,
})
    .strict();
// --- Assumptions contract ---------------------------------------------------
/**
 * The plausible-growth ceiling shared by the wrapper-assumption clamp, the
 * engine's `implausible-growth-rate` warning and the builder's inline nudge.
 * Per-item overrides may exceed it (up to the hard ±10,000 bps contract cap)
 * but never silently: the projection flags every rate above this line.
 */
export const PLAUSIBLE_GROWTH_CEILING_BPS = 2000;
/** Nominal annual growth per wrapper, integer basis points. ±20% cap. */
const growthBpsSchema = bpsSchema
    .min(-PLAUSIBLE_GROWTH_CEILING_BPS, 'Growth cannot be below -2000 bps (-20%)')
    .max(PLAUSIBLE_GROWTH_CEILING_BPS, 'Growth cannot exceed 2000 bps (+20%)');
/**
 * Growth assumptions keyed by wrapper. ALL eight wrappers are required so a
 * projection can never silently fall back to an implicit default for a
 * wrapper the adviser forgot to set.
 */
export const growthBpsByWrapperSchema = z
    .object({
    cash: growthBpsSchema,
    cash_isa: growthBpsSchema,
    stocks_isa: growthBpsSchema,
    lisa: growthBpsSchema,
    jisa: growthBpsSchema,
    nsi: growthBpsSchema,
    gia: growthBpsSchema,
    dc_pension: growthBpsSchema,
    db_pension: growthBpsSchema,
    state_pension: growthBpsSchema,
    property: growthBpsSchema,
    business: growthBpsSchema,
    other_asset: growthBpsSchema,
})
    .strict();
/**
 * The assumption set a projection runs under. Rates are integer basis points
 * (never fractional percentages); ages and horizons are integer years.
 */
export const assumptionsSchema = z
    .object({
    growthBpsByWrapper: growthBpsByWrapperSchema,
    inflationBps: bpsSchema.min(0).max(2000),
    wageGrowthBps: bpsSchema.min(0).max(2000),
    retirementAge: z.number().int().min(40).max(75),
    statePensionAge: z.number().int().min(60).max(75),
    horizonYears: z.number().int().min(1).max(70),
})
    .strict();
/**
 * Per-scenario assumption overrides: every `Assumptions` field is optional,
 * derived from the same schema so there is exactly one source of truth (no
 * hand-written `Partial<Assumptions>` casts). Still strict — unknown keys are
 * rejected.
 */
export const assumptionOverridesSchema = assumptionsSchema.partial();
// --- Plan objects -----------------------------------------------------------
const planLabelSchema = z.string().trim().min(1, 'A label is required').max(120);
const planYearSchema = z.number().int().min(2000).max(2120);
/**
 * A held asset inside a tax wrapper, with an optional regular contribution.
 * `rentalYieldBps` applies to `property` wrapper assets only: a non-null
 * value marks the property as buy-to-let and the projection engine CONSUMES
 * it (gross rent = yield × value each year, taxed as rental income).
 */
export const assetSchema = z
    .object({
    id: z.string().uuid(),
    clientId: clientIdSchema,
    wrapper: wrapperSchema,
    label: planLabelSchema,
    currentAmountPence: nonNegativePenceSchema,
    contributionPence: nonNegativePenceSchema,
    contributionFrequency: frequencySchema,
    rentalYieldBps: bpsSchema
        .min(0, 'Rental yield cannot be negative')
        .max(2000, 'Rental yield cannot exceed 2000 bps (20%)')
        .nullable()
        .default(null),
})
    .strict()
    .refine((a) => a.rentalYieldBps === null || a.wrapper === 'property', {
    message: 'Rental yield applies to property assets only',
    path: ['rentalYieldBps'],
});
/**
 * A recurring inflow. `startYear`/`endYear` of `null` mean "from the start of
 * the projection" / "open-ended"; `endsAtRetirement` truncates at the
 * assumed retirement age regardless of `endYear`.
 */
export const incomeStreamSchema = z
    .object({
    id: z.string().uuid(),
    clientId: clientIdSchema,
    kind: incomeKindSchema,
    label: planLabelSchema,
    amountPence: nonNegativePenceSchema,
    frequency: frequencySchema,
    startYear: planYearSchema.nullable(),
    endYear: planYearSchema.nullable(),
    endsAtRetirement: z.boolean(),
})
    .strict();
/** A recurring spending line. */
export const spendingItemSchema = z
    .object({
    id: z.string().uuid(),
    clientId: clientIdSchema,
    kind: spendingKindSchema,
    label: planLabelSchema,
    amountPence: nonNegativePenceSchema,
    frequency: frequencySchema,
    startYear: planYearSchema.nullable(),
    endYear: planYearSchema.nullable(),
})
    .strict();
/**
 * An outstanding liability. `repaymentPence` is the regular repayment per
 * month; `interestRateBps` is the annual rate in integer basis points.
 */
export const liabilitySchema = z
    .object({
    id: z.string().uuid(),
    clientId: clientIdSchema,
    kind: liabilityKindSchema,
    label: planLabelSchema,
    outstandingPence: nonNegativePenceSchema,
    interestRateBps: bpsSchema.min(0).max(3000),
    repaymentPence: nonNegativePenceSchema,
    /** Year the debt begins (a mid-plan purchase); null = already held. */
    startYear: planYearSchema.nullable().default(null),
    endYear: planYearSchema.nullable(),
})
    .strict();
/**
 * A one-off life event in a given year. `direction` says which way the cash
 * moves; `wrapper` (nullable) says which wrapper it hits, e.g. an inheritance
 * landing in the GIA.
 */
export const lifeEventSchema = z
    .object({
    id: z.string().uuid(),
    clientId: clientIdSchema,
    kind: lifeEventKindSchema,
    label: planLabelSchema,
    year: planYearSchema,
    amountPence: nonNegativePenceSchema,
    direction: lifeEventDirectionSchema,
    wrapper: wrapperSchema.nullable(),
    note: z.string().max(1000).nullable(),
})
    .strict();
/**
 * A named what-if scenario: assumption overrides layered on the base
 * assumptions, plus extra life events. The plan is never mutated.
 */
export const scenarioSchema = z
    .object({
    id: z.string().uuid(),
    clientId: clientIdSchema,
    name: z.string().trim().min(1, 'A scenario name is required').max(120),
    description: z.string().max(1000).nullable(),
    assumptionOverrides: assumptionOverridesSchema,
    addedLifeEvents: z.array(lifeEventSchema),
})
    .strict();
/** A client's complete modelled plan: everything the projection consumes. */
export const clientPlanSchema = z
    .object({
    clientId: clientIdSchema,
    assets: z.array(assetSchema),
    incomeStreams: z.array(incomeStreamSchema),
    spendingItems: z.array(spendingItemSchema),
    liabilities: z.array(liabilitySchema),
    lifeEvents: z.array(lifeEventSchema),
})
    .strict();
// --- Fact-find contracts (DB-aligned, scenario-scoped) ----------------------
//
// These are the wire contracts for the persisted fact-find path
// (POST/GET/PATCH under /api/v1/clients/:id/scenarios and
// /api/v1/scenarios/:id/...). They mirror the 0004/0005 tables exactly:
// scenario-scoped, `owner` = client|partner|joint, amounts in integer pence,
// rates in integer basis points. `scenarioId` / `firmId` are NEVER accepted in
// a body — they come from the URL path and the verified session respectively.
// The clientId-keyed plan schemas above remain the web fixture model.
export const holdingOwnerSchema = z.nativeEnum(HoldingOwner);
export const assetTypeSchema = z.nativeEnum(AssetType);
export const incomeStreamTypeSchema = z.nativeEnum(IncomeStreamType);
export const spendingCategorySchema = z.nativeEnum(SpendingCategory);
export const liabilityTypeSchema = z.nativeEnum(LiabilityType);
export const scenarioStatusSchema = z.nativeEnum(ScenarioStatus);
export const scenarioIdSchema = z.string().uuid();
const itemNameSchema = z.string().trim().min(1, 'A name is required').max(200);
/** Annual growth override in integer basis points; null = scenario default. */
const growthRateBpsSchema = bpsSchema
    .min(-2000, 'Growth cannot be below -2000 bps (-20%)')
    .max(2000, 'Growth cannot exceed 2000 bps (+20%)')
    .nullable();
/** Require at least one field on PATCH so an empty body is a 422, not a no-op. */
function atLeastOneField(schema) {
    return schema.partial().refine((value) => Object.values(value).some((v) => v !== undefined), {
        message: 'At least one field must be provided',
    });
}
/** Read-side columns every 0005 fact-find row carries. */
const factFindRowReadShape = {
    id: z.string().uuid(),
    firmId: firmIdSchema,
    scenarioId: scenarioIdSchema,
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
    deletedAt: isoInstantSchema.nullable(),
};
// --- scenarios ---------------------------------------------------------------
/** Create a scenario for a client. The client comes from the URL, not the body. */
export const scenarioCreateSchema = z
    .object({
    name: z.string().trim().min(1, 'A scenario name is required').max(120),
    description: z.string().max(1000).nullable().default(null),
    isBaseline: z.boolean().default(false),
    startYear: z.number().int().min(2000).max(2120),
    projectionYears: z
        .number()
        .int()
        .min(1, 'Projection must cover at least 1 year')
        .max(100, 'Projection cannot exceed 100 years'),
    /**
     * The review this draft was started against — the count of completed
     * reviews the caller observed (see `reviewLockSchema.reviewEpoch`).
     *
     * REQUIRED, and deliberately not optional: an optional precondition is one
     * a stale tab satisfies by omitting it. Every other plan write proves which
     * revision it was composed against; creation could not, because a new row
     * has no `updatedAt` to compare, so a draft begun during one review joined
     * the next one silently.
     */
    expectedReviewEpoch: z.number().int().min(0),
})
    .strict();
/** A scenario as returned on read. */
export const scenarioReadSchema = z.object({
    id: scenarioIdSchema,
    firmId: firmIdSchema,
    clientId: clientIdSchema,
    name: z.string(),
    description: z.string().nullable(),
    isBaseline: z.boolean(),
    status: scenarioStatusSchema,
    startYear: z.number().int(),
    projectionYears: z.number().int(),
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
    deletedAt: isoInstantSchema.nullable(),
    /**
     * D2.2 — the adviser's own presentation state for this scenario.
     *
     * `visible` is NOT `deletedAt`: hiding a scenario from the picker leaves its
     * definition in every comparison and in the provenance of any report that
     * already named it. `includeInReport` defaults false, so a scenario is in a
     * client's document only because the adviser put it there.
     */
    visible: z.boolean(),
    sortOrder: z.number().int(),
    includeInReport: z.boolean(),
    /**
     * Workspace v2 §12, A20 — drawn in Scenario comparison. A separate choice
     * from `includeInReport`: comparing a scenario never puts it in a client's
     * presentation, and including one never draws it. False on a database that
     * has not applied migration 0047.
     */
    inComparison: z.boolean(),
    /**
     * UX-38 — true when this database stores the Compare choice (migration 0047),
     * so `inComparison: false` on every row means "none compared", not "no choice
     * saved". Optional so a response from an older API still parses; absent means
     * the web cannot tell the two apart and falls back to this device's memory.
     */
    comparisonStored: z.boolean().optional(),
});
/** At most the recorded plan plus this many scenarios are compared at once (§12). */
export const MAX_COMPARED_SCENARIOS = 2;
/**
 * D2.2 — the presentation state an adviser sets on a scenario. Separate from
 * `scenarioUpdateSchema` (name/description/definition) because these three are
 * a different KIND of change: they alter what a client is shown, not what the
 * model computes, and the export reads them.
 */
export const scenarioMutationRevisionSchema = z
    .object({
    expectedUpdatedAt: isoInstantSchema,
})
    .strict();
export const scenarioPresentationUpdateSchema = z
    .object({
    expectedUpdatedAt: isoInstantSchema,
    visible: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    includeInReport: z.boolean().optional(),
    inComparison: z.boolean().optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.visible === undefined &&
        body.sortOrder === undefined &&
        body.includeInReport === undefined &&
        body.inComparison === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Provide at least one field to update',
        });
    }
    /**
     * A hidden scenario cannot be in the client's report. The two settings are
     * independent columns and nothing in SQL stops the pair, so an adviser who
     * hid a scenario and forgot to untick it would send a client a document
     * containing the very scenario they had taken off their own screen.
     */
    if (body.visible === false && body.includeInReport === true) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['includeInReport'],
            message: 'A hidden scenario cannot be included in the report',
        });
    }
    // Nor drawn in Scenario comparison: a hidden scenario is on no chart.
    if (body.visible === false && body.inComparison === true) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['inComparison'],
            message: 'A hidden scenario cannot be compared',
        });
    }
});
// --- assets ------------------------------------------------------------------
const assetWriteShape = z
    .object({
    owner: holdingOwnerSchema,
    type: assetTypeSchema,
    name: itemNameSchema,
    currentValuePence: nonNegativePenceSchema,
    monthlyContributionPence: nonNegativePenceSchema.default(0),
    growthRateBps: growthRateBpsSchema.default(null),
})
    .strict();
export const assetCreateSchema = assetWriteShape;
export const assetUpdateSchema = atLeastOneField(assetWriteShape);
export const assetReadSchema = z.object({
    ...factFindRowReadShape,
    owner: holdingOwnerSchema,
    type: assetTypeSchema,
    name: z.string(),
    currentValuePence: nonNegativePenceSchema,
    monthlyContributionPence: nonNegativePenceSchema,
    growthRateBps: bpsSchema.nullable(),
});
// --- income streams ------------------------------------------------------------
const incomeStreamWriteShape = z
    .object({
    owner: holdingOwnerSchema,
    type: incomeStreamTypeSchema,
    name: itemNameSchema,
    amountPence: nonNegativePenceSchema,
    frequency: frequencySchema,
    startDate: isoDateSchema.nullable().default(null),
    endDate: isoDateSchema.nullable().default(null),
    growthRateBps: growthRateBpsSchema.default(null),
    taxable: z.boolean().default(true),
})
    .strict();
export const incomeStreamCreateSchema = incomeStreamWriteShape;
export const incomeStreamUpdateSchema = atLeastOneField(incomeStreamWriteShape);
export const incomeStreamReadSchema = z.object({
    ...factFindRowReadShape,
    owner: holdingOwnerSchema,
    type: incomeStreamTypeSchema,
    name: z.string(),
    amountPence: nonNegativePenceSchema,
    frequency: frequencySchema,
    startDate: isoDateSchema.nullable(),
    endDate: isoDateSchema.nullable(),
    growthRateBps: bpsSchema.nullable(),
    taxable: z.boolean(),
});
// --- spending items ------------------------------------------------------------
const spendingItemWriteShape = z
    .object({
    category: spendingCategorySchema,
    name: itemNameSchema,
    amountPence: nonNegativePenceSchema,
    frequency: frequencySchema,
    startDate: isoDateSchema.nullable().default(null),
    endDate: isoDateSchema.nullable().default(null),
    inflationLinked: z.boolean().default(true),
})
    .strict();
export const spendingItemCreateSchema = spendingItemWriteShape;
export const spendingItemUpdateSchema = atLeastOneField(spendingItemWriteShape);
export const spendingItemReadSchema = z.object({
    ...factFindRowReadShape,
    category: spendingCategorySchema,
    name: z.string(),
    amountPence: nonNegativePenceSchema,
    frequency: frequencySchema,
    startDate: isoDateSchema.nullable(),
    endDate: isoDateSchema.nullable(),
    inflationLinked: z.boolean(),
});
// --- liabilities -----------------------------------------------------------------
const liabilityWriteShape = z
    .object({
    owner: holdingOwnerSchema,
    type: liabilityTypeSchema,
    name: itemNameSchema,
    balancePence: nonNegativePenceSchema,
    interestRateBps: bpsSchema.min(0).max(3000),
    monthlyPaymentPence: nonNegativePenceSchema.default(0),
    endDate: isoDateSchema.nullable().default(null),
})
    .strict();
export const liabilityCreateSchema = liabilityWriteShape;
export const liabilityUpdateSchema = atLeastOneField(liabilityWriteShape);
export const liabilityReadSchema = z.object({
    ...factFindRowReadShape,
    owner: holdingOwnerSchema,
    type: liabilityTypeSchema,
    name: z.string(),
    balancePence: nonNegativePenceSchema,
    interestRateBps: bpsSchema,
    monthlyPaymentPence: nonNegativePenceSchema,
    endDate: isoDateSchema.nullable(),
});
// --- aggregated fact-find read ----------------------------------------------------
/** Everything entered for a scenario, in one response. */
export const factFindReadSchema = z.object({
    scenarioId: scenarioIdSchema,
    assets: z.array(assetReadSchema),
    incomeStreams: z.array(incomeStreamReadSchema),
    spendingItems: z.array(spendingItemReadSchema),
    liabilities: z.array(liabilityReadSchema),
});
// --- Projection contract --------------------------------------------------
/**
 * One recurring or one-off cash-flow line. Structurally compatible with the
 * engine's `CashFlowItem`, so a validated request can be handed straight to
 * `project()` without a hand-written adapter type.
 */
export const cashFlowItemSchema = z
    .object({
    label: z.string().trim().min(1, 'A label is required').max(200),
    amount: nonNegativePenceSchema,
    frequency: frequencySchema,
    startYear: z.number().int().min(1900).max(2200),
    endYear: z.number().int().min(1900).max(2200).nullable(),
})
    .strict();
/** The payload the API accepts to run a projection. Mirrors `ProjectionInput`. */
export const projectionRequestSchema = z
    .object({
    horizonYears: z
        .number()
        .int()
        .min(1, 'Horizon must be at least 1 year')
        .max(100, 'Horizon cannot exceed 100 years'),
    openingBalance: penceSchema,
    incomes: z.array(cashFlowItemSchema),
    expenses: z.array(cashFlowItemSchema),
})
    .strict();
/** One projected year. Mirrors the engine's `ProjectionYear` (integer pence). */
export const projectionYearSchema = z.object({
    year: z.number().int(),
    income: penceSchema,
    expenditure: penceSchema,
    closingBalance: penceSchema,
});
/**
 * A warning about an entered item that contributed nothing. Mirrors the engine's
 * `ProjectionWarning` so an inert item is carried to the client, not dropped.
 */
export const projectionWarningSchema = z.object({
    code: z.enum(['item-window-degenerate', 'item-outside-horizon']),
    itemLabel: z.string(),
    message: z.string(),
});
/** The projection result returned on read. Mirrors the engine `ProjectionResult`. */
export const projectionResponseSchema = z.object({
    engineVersion: z.string(),
    asOfDate: isoDateSchema,
    years: z.array(projectionYearSchema),
    depletionYear: z.number().int().nullable(),
    warnings: z.array(projectionWarningSchema),
});
// --- Wealth projection contract ---------------------------------------------
/**
 * The payload the API accepts to run a per-wrapper wealth projection. The
 * clock is injected via `asOfDate`/`startYear` — the engine never calls
 * `new Date()`.
 */
export const wealthProjectionRequestSchema = z
    .object({
    clientId: clientIdSchema,
    asOfDate: isoDateSchema,
    startYear: z.number().int().min(1900).max(2200),
    currentAge: z.number().int().min(0).max(110),
    assumptions: assumptionsSchema,
    plan: clientPlanSchema,
})
    .strict();
/** End-of-year holdings per wrapper. Each is a non-negative integer of pence. */
export const wrapperBalancesSchema = z.object({
    cash: nonNegativePenceSchema,
    cash_isa: nonNegativePenceSchema,
    stocks_isa: nonNegativePenceSchema,
    lisa: nonNegativePenceSchema,
    jisa: nonNegativePenceSchema,
    nsi: nonNegativePenceSchema,
    gia: nonNegativePenceSchema,
    dc_pension: nonNegativePenceSchema,
    db_pension: nonNegativePenceSchema,
    state_pension: nonNegativePenceSchema,
    property: nonNegativePenceSchema,
    business: nonNegativePenceSchema,
    other_asset: nonNegativePenceSchema,
});
/**
 * One projected year of the wealth projection. `totalNetWorthPence` is signed
 * (integer pence) — it goes below zero when liabilities exceed assets.
 */
export const wealthProjectionYearSchema = z.object({
    year: z.number().int(),
    age: z.number().int(),
    balancesByWrapper: wrapperBalancesSchema,
    liabilitiesOutstandingPence: nonNegativePenceSchema,
    totalNetWorthPence: penceSchema,
    grossIncomePence: nonNegativePenceSchema,
    spendingPence: nonNegativePenceSchema,
    illustrativeTaxPence: nonNegativePenceSchema,
});
/** A warning the wealth projection surfaces alongside its years. */
export const wealthProjectionWarningSchema = z.object({
    code: z.string(),
    message: z.string(),
});
/** The wealth projection result returned on read. */
export const wealthProjectionResultSchema = z.object({
    engineVersion: z.string(),
    /** The reviewed tax rule-set revision the figures were computed under. */
    taxRulesVersion: z.string().min(1),
    asOfDate: isoDateSchema,
    years: z.array(wealthProjectionYearSchema),
    depletionYear: z.number().int().nullable(),
    warnings: z.array(wealthProjectionWarningSchema),
});
// --- Projection run contract (persisted runs) -------------------------------
/**
 * Request body for running a projection over a persisted scenario. The
 * valuation date is INJECTED (the engine never calls `new Date()`); when
 * omitted, the API supplies its own clock at the boundary.
 */
export const projectionRunRequestSchema = z
    .object({
    asOfDate: isoDateSchema.optional(),
})
    .strict();
/**
 * A warning attached to a persisted run: either an engine warning or a
 * DB->engine mapping warning ("captured but not modelled" surfacing). Codes
 * are machine-readable; the reporting layer re-words them for lay readers.
 */
export const projectionRunWarningSchema = z.object({
    code: z.string().min(1),
    severity: z.enum(['info', 'warning', 'critical']),
    message: z.string(),
    year: z.number().int().nullable(),
});
/**
 * A persisted projection run as returned on read. Carries the reproducibility
 * stamps (input snapshot reference, tax-rules / engine / code versions) with
 * the year-by-year result.
 */
export const projectionRunReadSchema = z.object({
    id: z.string().uuid(),
    firmId: firmIdSchema,
    scenarioId: scenarioIdSchema,
    scenarioVersionId: z.string().uuid(),
    status: z.enum(['queued', 'running', 'succeeded', 'failed']),
    asOfDate: isoDateSchema,
    taxRulesVersion: z.string().min(1),
    engineVersion: z.string().min(1),
    codeVersion: z.string().min(1),
    years: z.array(projectionYearSchema),
    depletionYear: z.number().int().nullable(),
    warnings: z.array(projectionRunWarningSchema),
    createdAt: isoInstantSchema,
});
// --- Auth contract -----------------------------------------------------------
/**
 * Credential login request. Used by the DEV-ONLY credential provider today;
 * shaped so a real password grant (or Cognito adapter) can reuse it later.
 * `.strict()` — an auth payload must never carry extra fields.
 */
export const authLoginRequestSchema = z
    .object({
    email: z.string().email(),
    password: z.string().min(1, 'A password is required'),
})
    .strict();
/** A successful login: the bearer token the API's AuthPort will verify. */
export const authTokenResponseSchema = z.object({
    accessToken: z.string().min(1),
    tokenType: z.literal('Bearer'),
});
/**
 * Complete a software-token (TOTP) challenge from the login step. The SHARED
 * request body for BOTH challenge endpoints — `POST /api/v1/auth/mfa` (a
 * returning user) and `POST /api/v1/auth/mfa-setup` (a first-login enrolment) —
 * which is why the client always sends the same three fields. `email` matches
 * how the login route identifies the user (see `authLoginRequestSchema`).
 * `session` is the opaque, short-lived Cognito challenge session from the login
 * response, carried by the client and never minted here. `.strict()` — an auth
 * payload carries no extras.
 */
export const authRespondChallengeRequestSchema = z
    .object({
    email: z.string().email(),
    // A TOTP code is always exactly six digits; reject anything else at the edge
    // with a field-level 422 rather than round-tripping a malformed code to Cognito.
    code: z.string().regex(/^\d{6}$/, 'A 6-digit authentication code is required'),
    session: z.string().min(1),
})
    .strict();
/**
 * A completed sign-in. `idToken` is the bearer the API's AuthPort verifies
 * (`tokenUse: 'id'`); `accessToken` is the companion Cognito access token; and
 * `expiresIn` is the ID token's lifetime in seconds. This SAME shape is returned
 * everywhere a token is finally issued: the login step's `ok` branch (only
 * reachable on an MFA-less pool — ours requires MFA) AND both challenge
 * endpoints (`/auth/mfa`, `/auth/mfa-setup`) once the code is verified.
 *
 * NO refresh token is returned to the browser: a long-lived refresh token in
 * JS-reachable state is an XSS-exfiltration risk. Token renewal, if added, is a
 * separate server-mediated decision, not a field handed to the SPA here.
 */
export const authLoginOkSchema = z
    .object({
    status: z.literal('ok'),
    accessToken: z.string().min(1),
    idToken: z.string().min(1),
    tokenType: z.literal('Bearer'),
    // Seconds until the ID token expires (Cognito AuthenticationResult.ExpiresIn).
    expiresIn: z.number().int().positive(),
})
    .strict();
/**
 * Self-service password reset (GOAL 2026-09-13 item 4). Two public steps:
 * `POST /api/v1/auth/password-reset/request` asks Cognito to email a one-time
 * code to the account's registered address, and `…/confirm` exchanges that
 * code plus a NEW password for a reset. Neither step weakens MFA: the next
 * sign-in still demands the authenticator. The request step's response is the
 * same for a known and an unknown address — no account oracle.
 */
export const authPasswordResetRequestSchema = z
    .object({
    email: z.string().email(),
})
    .strict();
export const authPasswordResetRequestedSchema = z
    .object({ status: z.literal('requested') })
    .strict();
/**
 * ONE password rule for every place a person chooses a password (invite
 * accept, self-service reset). Mirrors the pool's policy so a weak password is
 * refused at the edge with a field message rather than after a Cognito round
 * trip; the pool remains the authority and still 422s anything it dislikes.
 */
export const passwordPolicySchema = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(256)
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/[0-9]/, 'Password must contain a number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain a symbol');
export const authPasswordResetConfirmSchema = z
    .object({
    email: z.string().email(),
    // Cognito's verification code is six digits; reject anything else at the
    // edge rather than round-tripping it.
    code: z.string().regex(/^\d{6}$/, 'A 6-digit code is required'),
    newPassword: passwordPolicySchema,
})
    .strict();
export const authPasswordResetDoneSchema = z
    .object({ status: z.literal('password_reset') })
    .strict();
/** `POST /auth/logout` — the refresh cookie is dropped; nothing else to say. */
export const authSignOutResponseSchema = z.object({ status: z.literal('signed_out') }).strict();
/**
 * `POST /api/v1/demo-requests` — the public marketing lead form (2026-08-23
 * review P0 #5: the form was a stub that discarded every lead while promising
 * a response). Strict lengths keep the public surface small; the email check
 * is deliberately shallow — the follow-up conversation validates deliverability
 * better than any regex.
 */
export const demoRequestRequestSchema = z
    .object({
    prospectName: z.string().trim().min(1).max(200),
    firmName: z.string().trim().min(1).max(200),
    email: z.string().trim().email().max(320),
    /** What the prospect would like to explore — optional, carried through to
     *  the lead row and the notification (2026-09-11). Omitted when blank. */
    message: z.string().trim().min(1).max(2000).optional(),
})
    .strict();
export const demoRequestResponseSchema = z.object({ status: z.literal('received') }).strict();
/**
 * Outcome of the server-mediated Cognito login (step one): a completed sign-in
 * (`ok`), or one of two software-token MFA challenges the client must answer
 * next. A discriminated union on `status` so the client cannot confuse them.
 *
 * NEITHER challenge branch carries a bearer token: a password alone must never
 * yield a session (MFA hard rule). Each carries only the opaque challenge
 * `session` to hand back — `mfa_setup` additionally returns the freshly-
 * associated TOTP `secret` (render as a QR) for the first-login enrolment.
 */
export const authLoginResponseSchema = z.discriminatedUnion('status', [
    authLoginOkSchema,
    // Returning user with TOTP already enrolled: answer the 6-digit code at
    // POST /api/v1/auth/mfa.
    z
        .object({
        status: z.literal('mfa_required'),
        session: z.string().min(1),
    })
        .strict(),
    // First login: no authenticator enrolled yet. `secret` is the freshly-
    // associated TOTP secret (render as a QR); `session` carries the enrolment to
    // POST /api/v1/auth/mfa-setup. Still NO bearer token — enrolment is not yet a
    // completed session.
    z
        .object({
        status: z.literal('mfa_setup'),
        secret: z.string().min(1),
        session: z.string().min(1),
    })
        .strict(),
]);
/** `GET /api/v1/auth/me` — the verified session behind the bearer token, so the
 * web app can establish its identity without decoding an opaque token. */
export const firmAccessSchema = z
    .object({
    state: z.enum(['full', 'grace', 'read_only', 'suspended']),
    source: z.enum(['operator', 'subscription', 'none']),
    reason: z.string().nullable(),
})
    .strict();
export const authSessionResponseSchema = z
    .object({
    firmId: firmIdSchema,
    adviserId: adviserIdSchema,
    role: adviserRoleSchema,
    mfaVerified: z.boolean(),
    /** The firm's access state, as the API's gate read it for this request (0043). */
    access: firmAccessSchema.optional(),
})
    .strict();
// --- Report export contract -------------------------------------------------
/**
 * Everything the reporting package needs to render a client report. This is a
 * modelling AID, not advice — the renderer must always include the
 * illustrative-not-advice disclaimer identified by `disclaimerVersion`.
 * `auditReference` carries the audit-trail reference once the report run is
 * recorded ('AUDIT-PENDING' until then).
 */
export const reportExportSchema = z
    .object({
    client: clientReadSchema,
    firm: firmSchema,
    adviser: adviserProfileSchema,
    scenarioName: z.string().max(120).nullable(),
    generatedAsOf: isoDateSchema,
    engineVersion: z.string(),
    taxRulesVersion: z.string().min(1),
    assumptions: assumptionsSchema,
    projection: wealthProjectionResultSchema,
    auditReference: z.string().min(1),
    disclaimerVersion: z.string().min(1),
})
    .strict();
// --- Firm assumption presets (Firm settings) --------------------------------
/**
 * A firm's own version of one named assumption set (Firm settings tab,
 * feedback round 4). Same shape and bounds as the engine's built-in presets:
 * nominal, before fees, all integer basis points. `defaultFeeBps` is the
 * firm's platform+fund fee drag applied when an asset carries no explicit
 * fee override (the product default is zero).
 */
const presetClassRatesSchema = z
    .object({
    growthBps: bpsSchema
        .min(-2000, 'Growth cannot be below -2000 bps (-20%)')
        .max(2000, 'Growth cannot exceed 2000 bps (+20%)'),
    yieldBps: bpsSchema.min(0).max(2000, 'Yield cannot exceed 2000 bps (20%)'),
})
    .strict();
/**
 * The firm's own name for an assumption set (migration 0028).
 *
 * A LABEL, never an engine key: the canonical preset name stays
 * 'conservative' | 'moderate' | 'optimistic', so renaming a bundle moves no
 * figure and cannot restate a projection already shown to a client.
 *
 * Trimmed before validation so a name that is only whitespace is rejected
 * rather than stored and rendered as an unnamed set on a client's report.
 * `null` means "use the product label" — the same "unset, explicitly" idiom
 * the bundle itself uses.
 *
 * The advice-word refusal exists because this string prints VERBATIM beside
 * the figures on a client's report, and the static advice-language scanner
 * reads source files, not rows a firm typed into the database (review
 * finding, 2026-08-12). A set named "Recommended" would put
 * recommendation-shaped wording on a regulated-adjacent client output with
 * nothing else able to catch it, so the contract refuses the words rather
 * than trusting every future render site to. Word-boundary match: "Bestwood
 * Cautious" is a place name and passes; "Best Growth" does not.
 */
// "optima[l]" not "optimal": the T8 advice-language scanner reads this SOURCE
// file, and the char-class keeps this guard's own word list from tripping it —
// the matched strings are identical.
const ADVICE_WORDS_IN_LABEL = /\b(recommend(?:ed|ation|s)?|advice|advis(?:e|ed|ory)|best|optima[l]|optimis(?:e|ed|t)|should|suitab(?:le|ility)|guaranteed?)\b/i;
/** True when `text` carries advice-like wording a client-facing name must not. */
export function containsAdviceWording(text) {
    return ADVICE_WORDS_IN_LABEL.test(text);
}
export const firmPresetLabelSchema = z
    .string()
    .transform((s) => s.trim())
    .pipe(z
    .string()
    .min(1, 'A custom name cannot be blank')
    .max(40, 'A custom name cannot be longer than 40 characters')
    .refine((s) => !ADVICE_WORDS_IN_LABEL.test(s), {
    message: 'This name appears on client reports, so it cannot contain advice-like words (e.g. "recommended", "best", "guaranteed") — the tool is a modelling aid, not advice',
}));
export const firmAssumptionPresetSchema = z
    .object({
    inflationBps: bpsSchema.min(0).max(2000),
    rpiBps: bpsSchema.min(0).max(2000),
    wageGrowthBps: bpsSchema.min(0).max(2000),
    equity: presetClassRatesSchema,
    bonds: presetClassRatesSchema,
    cash: presetClassRatesSchema,
    property: presetClassRatesSchema,
    defaultFeeBps: bpsSchema.min(0).max(500, 'Fee cannot exceed 500 bps (5%)'),
    /** The firm's own name for this set; null = use the product label. */
    displayLabel: firmPresetLabelSchema.nullable().default(null),
})
    .strict();
/**
 * The key of a firm-created assumption set: `custom:<uuid>`. Matches the CHECK
 * in migration 0030, so the contract and the column agree about what a custom
 * key looks like rather than each having its own opinion.
 */
export const firmCustomSetKeySchema = z
    .string()
    .regex(/^custom:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'A custom assumption set is keyed custom:<uuid>');
/**
 * A firm-created assumption set (feedback 2026-08-14: "add a feature to add
 * more custom assumption sets not just conservative, moderate, optimistic,
 * they should be able to add as many as they like").
 *
 * Identical to a built-in bundle except that its NAME IS REQUIRED. The three
 * built-ins fall back to a product label when `displayLabel` is null; a custom
 * set has no product label to fall back to, so a nameless one would render as
 * a blank option everywhere it appears. Migration 0030 enforces the same rule
 * in SQL.
 */
export const firmCustomAssumptionSetSchema = firmAssumptionPresetSchema
    .omit({ displayLabel: true })
    .extend({
    key: firmCustomSetKeySchema,
    /** Required, and still barred from advice-like wording — it reaches reports. */
    displayLabel: firmPresetLabelSchema,
})
    .strict();
/**
 * How many custom sets one firm may hold. A bound, not a judgement about how
 * many are useful: this payload is read on every builder load, and an unbounded
 * array is an unbounded response. Well above what the feedback describes
 * ("as many as they like") for any real firm.
 */
export const FIRM_CUSTOM_SET_LIMIT = 50;
/**
 * The firm's assumption sets: the three product bundles, plus any number of the
 * firm's own. `null` on a built-in means "use the product default for this
 * preset" — reverting is an explicit act, never a merge of half-set fields, so
 * a stored bundle is always complete.
 *
 * `custom` defaults to `[]` so a row stored before 2026-08-14 still parses:
 * every firm's saved presets predate this field, and a required array would
 * have failed every one of them on read.
 */
export const firmAssumptionPresetsSchema = z
    .object({
    conservative: firmAssumptionPresetSchema.nullable(),
    moderate: firmAssumptionPresetSchema.nullable(),
    optimistic: firmAssumptionPresetSchema.nullable(),
    custom: z
        .array(firmCustomAssumptionSetSchema)
        .max(FIRM_CUSTOM_SET_LIMIT, `A firm can hold at most ${FIRM_CUSTOM_SET_LIMIT} custom sets`)
        .default([])
        .refine((sets) => new Set(sets.map((s) => s.key)).size === sets.length, {
        message: 'Two custom assumption sets cannot share a key',
    })
        .refine((sets) => new Set(sets.map((s) => s.displayLabel.trim().toLowerCase())).size === sets.length, {
        // Two sets called "Cantab Balanced" are indistinguishable on the
        // dropdown that picks between them, and on the report that names one.
        message: 'Two custom assumption sets cannot share a name',
    }),
})
    .strict();
/** PUT body and GET/PUT response payload for the firm-assumptions route. */
export const firmAssumptionPresetsUpdateSchema = firmAssumptionPresetsSchema;
export const firmAssumptionPresetsResponseSchema = z
    .object({ presets: firmAssumptionPresetsSchema })
    .strict();
// --- Firm modelling defaults (Firm settings) --------------------------------
/**
 * The named assumption sets a firm can nominate as its starting point. The
 * same three names the engine ships; `custom` is deliberately excluded — a
 * firm-wide default must resolve to a bundle that actually exists.
 */
export const firmDefaultPresetNameSchema = z.enum(['conservative', 'moderate', 'optimistic']);
/**
 * What each assumption set is CALLED, as opposed to what it is keyed.
 *
 * One source of truth because there were three, and they disagreed on a
 * client-facing surface: the engine labelled `moderate` "Base case", the
 * builder title-cased the wire key to "Moderate", and the report's settings
 * summary printed the raw key `moderate`. An adviser and a client could read
 * three different names for one bundle in a single meeting.
 *
 * The KEY stays `moderate` — it is in saved plans, stored `assumptions_snapshot`
 * rows and the API contract, so renaming the wire value would strand them.
 * "Base case" states what the bundle is without ranking the other two against
 * it, which is what keeps the naming clear of the non-advice line: neither
 * "Moderate" nor "Balanced" should read as the sensible middle choice.
 *
 * A firm's own `displayLabel` overrides these wherever it is available; this
 * is the fallback when it is not set.
 */
export const PRESET_DISPLAY_LABELS = Object.freeze({
    conservative: 'Conservative',
    moderate: 'Base case',
    optimistic: 'Optimistic',
});
/**
 * The bundle's display name, given the raw stored value. Unknown values —
 * including a `custom:<uuid>` key from a future contract widening — are
 * returned unchanged rather than crashing a report render; the guard against
 * a raw key reaching a client is the caller's, and is asserted in
 * `settings-summary`'s own tests.
 */
export function presetDisplayLabel(preset) {
    return PRESET_DISPLAY_LABELS[preset] ?? preset;
}
/**
 * The firm's OWN name for a bundle — `"<label> (firm)"` — or undefined when the
 * firm has not renamed it.
 *
 * The one derivation, used by the builder's picker, the PDF's settings summary,
 * the PowerPoint deck and the engine's assumptions table. It lives here because
 * every time it was re-derived locally the surfaces drifted: the deck said
 * "Conservative" while the PDF from the same meeting said "Alpha Cautious
 * (firm)", and before that the builder said "Moderate" while the report said
 * "moderate" and the engine said "Base case". A client asking which one their
 * plan actually ran on deserves one answer.
 *
 * "(firm)" is kept deliberately: it is what tells a reader of a client report
 * that these are the FIRM's figures rather than the product's, and that stays
 * true — and stays necessary — under a custom name.
 *
 * `presets` is loosely typed so callers can pass the wire payload, a stored
 * snapshot's presets, or null, without each one casting.
 */
export function firmSetLabelFor(preset, presets) {
    if (preset === undefined || presets == null)
        return undefined;
    const bundle = presets[preset];
    const label = bundle?.displayLabel;
    return label != null && label !== '' ? `${label} (firm)` : undefined;
}
/**
 * The firm's starting points for a NEWLY CREATED client (Firm settings). These
 * seed a new fact-find document only — they never re-run, re-date or otherwise
 * touch an existing client or a saved projection run, so a firm can change its
 * house view without silently restating work already presented to a client.
 *
 * `null` on either field means "use the product default" (projection end age
 * 90, assumption set `moderate`). As with the assumption sets above,
 * reverting is an explicit act — never a merge of half-set fields.
 *
 * Note what is deliberately ABSENT:
 *  - There is no forecast START date. The start is the session tax-rules basis,
 *    an (asOfDate, startYear) PAIR — the engine refuses a start year earlier
 *    than the tax year containing the as-of date, so a firm-writable start date
 *    would be a second, conflicting source of truth. Firm settings surfaces the
 *    active basis read-only instead.
 *  - There is no country-of-residence field. England, Wales and Northern
 *    Ireland resolve to ONE identical rest-of-UK rule set here, and Welsh LTT /
 *    Scottish LBTT are outside Phase 1, so a selector would change no figure
 *    while implying a treatment the engine does not apply. Scottish taxpayers
 *    remain hard-gated by `taxRegionSchema`.
 */
export const firmModellingDefaultsSchema = z
    .object({
    /**
     * Age of the FIRST person on the plan at which a new client's projection
     * ends. Bounded well inside the engine's 0–130 age range: a default below
     * 50 or above 120 is a data-entry error, not a house view.
     */
    defaultEndAtClientAge: z
        .number()
        .int()
        .min(50, 'A default projection end age below 50 is not a usable house default')
        .max(120, 'A default projection end age above 120 is not a usable house default')
        .nullable(),
    /** The assumption set a new client starts on. */
    defaultPresetName: firmDefaultPresetNameSchema.nullable(),
})
    .strict();
/**
 * The product defaults every firm falls back to. Kept here, beside the schema,
 * so the API, the builder and the settings page all read ONE definition of
 * "unset" rather than each restating 90 / moderate.
 */
export const PRODUCT_DEFAULT_END_AT_CLIENT_AGE = 90;
export const PRODUCT_DEFAULT_PRESET_NAME = 'moderate';
/**
 * The projection end ages offered in the UI. ONE list, shared by Firm settings
 * and the per-plan projection window, so a firm default can never be a value
 * the plan's own selector cannot display. Well inside the contract's 50–120
 * bounds — the bounds are the limit, this is the menu.
 */
export const PROJECTION_END_AGE_CHOICES = [80, 85, 90, 95, 100, 105];
/** Resolve a firm's defaults against the product defaults. Pure; total. */
export function resolveFirmModellingDefaults(defaults) {
    return {
        endAtClientAge: defaults?.defaultEndAtClientAge ?? PRODUCT_DEFAULT_END_AT_CLIENT_AGE,
        presetName: defaults?.defaultPresetName ?? PRODUCT_DEFAULT_PRESET_NAME,
    };
}
/** PUT body and GET/PUT response payload for the firm-defaults route. */
export const firmModellingDefaultsUpdateSchema = firmModellingDefaultsSchema;
export const firmModellingDefaultsResponseSchema = z
    .object({ defaults: firmModellingDefaultsSchema })
    .strict();
/**
 * Where the live projection sits relative to the fact-find. Note the contrast
 * with everything above it in this file: the firm settings are a HOUSE VIEW
 * that seeds a client's figures, whereas this is one person's arrangement of
 * their own screen and can change no number anywhere.
 *
 * In the combined workspace (spec 2026-09-13 v2 §5) the three names are the
 * chart's position relative to Client details:
 *
 * 'chart-top' — Above details. THE DEFAULT since v2 §7.5.
 * 'split'     — Beside details. Under 1200px the workspace renders Above for
 *               that session instead, and never rewrites the stored choice.
 * 'stacked'   — Below details.
 *
 * The stored names predate the v2 labels and are kept so an explicit earlier
 * choice is honoured: an adviser who saved 'split' still opens Beside details,
 * while an adviser who never chose opens Above.
 */
export const builderLayoutSchema = z.enum(['split', 'stacked', 'chart-top']);
export const PRODUCT_DEFAULT_BUILDER_LAYOUT = 'chart-top';
/**
 * Which visual treatment this adviser's app wears (feedback 2026-08-31: "add an
 * option to use a UI view similar to this — an extra customisation option, not
 * the default ... it should look the same unless in personal settings I select
 * the new UI").
 *
 * 'classic'  — the treatment the product has always painted. THE DEFAULT, and
 *              it must stay pixel-identical for anyone who never opts in.
 * 'spacious' — larger type, more generous spacing, pill-shaped controls and
 *              hairline row rules.
 *
 * THIS IS SPACING, TYPE AND SHAPE ONLY. It carries no colour of its own, and
 * that restraint is load-bearing rather than cosmetic: the accent belongs to
 * the FIRM (white-label), and `--accent*` is written as inline custom
 * properties on `.pc-brand-scope`, an ANCESTOR of the element that carries
 * these attributes. Any accent declared by a theme layer below that point wins
 * over the firm's own colour for the whole subtree and silently un-brands the
 * app, while every provider-level test stays green — the exact defect
 * `apps/web/src/branding/brandCascade.test.ts` exists to prevent. A firm whose
 * brand colour is green therefore sees a green 'spacious' app; the theme never
 * chooses the colour, only the shape.
 *
 * Nothing here reaches the engine, a projection or a stored snapshot, so it
 * cannot move a figure — and a client's report is NOT themed by it either: a
 * report is the firm's artefact, not one adviser's screen preference.
 */
export const uiThemeSchema = z.enum(['classic', 'spacious']);
export const PRODUCT_DEFAULT_UI_THEME = 'classic';
/**
 * Which chart colour ramp this adviser's SCREEN draws with (feedback
 * 2026-09-21: "to reconcile the red/green possible colour blindness, in
 * personal settings add a colour scheme option for colour blindness").
 *
 * 'standard'    — the owner's 2026-09-18 palette sheet, the ramp the product
 *                 has always drawn. THE DEFAULT; an adviser who never opts in
 *                 sees no colour change at all.
 * 'colour-safe' — the same ten entity slots re-stepped so every pair that is
 *                 adjacent in a stack or compared side by side separates for
 *                 deuteranopia, protanopia and tritanopia (validator run in
 *                 docs/design/chart-palette-colour-safe-2026-09-21/).
 *
 * THIS IS CHART SERIES COLOUR ONLY, and like `uiTheme` it carries no accent:
 * `--accent*` is written as inline custom properties on `.pc-brand-scope`, an
 * ANCESTOR of the element that carries these attributes, so the firm keeps
 * owning its white-label colour (`apps/web/src/branding/brandCascade.test.ts`).
 *
 * Nothing here reaches the engine, a projection or a stored snapshot, so it
 * cannot move a figure — and the client's PDF report is NOT re-coloured by it
 * either: a report is the firm's artefact, not one adviser's screen preference.
 */
export const chartColourSchemeSchema = z.enum(['standard', 'colour-safe']);
export const PRODUCT_DEFAULT_CHART_COLOUR_SCHEME = 'standard';
/**
 * The workspace arrangement (spec 2026-09-13 v2 §1.1–§1.2, §7.1 A).
 * 'combined' — Results, Client details, Scenario comparison and Presentation in
 *              one scrolling workspace. THE DEFAULT.
 * 'tabbed'   — one of the four destinations at a time.
 * Never inferred from an older preference: a stored 'split' does not mean tabs.
 */
export const workspaceModeSchema = z.enum(['combined', 'tabbed']);
export const PRODUCT_DEFAULT_WORKSPACE_MODE = 'combined';
/** Fact-find sections in the combined workspace (§7.1 C): all in one document, or one at a time. */
export const factFindNavigationSchema = z.enum(['continuous', 'single']);
export const PRODUCT_DEFAULT_FACT_FIND_NAVIGATION = 'continuous';
/**
 * The chart shown first when a client opens (§7.1 E). An initial preference
 * only — a metric chosen for a client in the open session takes precedence.
 * Spelled as the spec's contract names; the chart code's own 'cashflow' /
 * 'wealth' tokens are adapted at the one place that reads this.
 */
export const defaultChartMetricSchema = z.enum(['cash-flow', 'net-worth']);
export const PRODUCT_DEFAULT_CHART_METRIC = 'cash-flow';
/**
 * How recorded events are annotated on the chart (§7.1 F, §11.7). 'hidden'
 * hides recorded-event annotations ONLY — a spending-shortfall warning is not
 * an event and is never hidden by this.
 */
export const chartEventDisplaySchema = z.enum(['labelled', 'circles', 'hidden']);
export const PRODUCT_DEFAULT_CHART_EVENT_DISPLAY = 'labelled';
/** §7.1 G extra controls. */
export const PRODUCT_DEFAULT_SHOW_ASSET_CHARTS = false;
export const PRODUCT_DEFAULT_SHOW_RESULTS_SHORTCUT = true;
/**
 * One adviser's personal settings (feedback 2026-08-13: "a personal settings
 * for each individual account where you can change your layout preferences").
 *
 * `null` means "use the product default", the same convention the firm
 * settings above use. Every key is always present in a STORED or RETURNED
 * object; a WRITE names only the keys it changes (see the update schema below).
 *
 * Note what is deliberately ABSENT: the per-chart set-up. Chart views are keyed
 * by CLIENT (liveChartModel), so they are a fact about one client's charts and
 * not about the adviser; putting them here would smuggle client-scoped state
 * into an account-scoped row. They stay in per-browser storage until they earn
 * a client-scoped contract of their own.
 *
 * Nothing in this object is client data or an engine input. A missing or
 * unreadable preferences row must therefore never block work — every consumer
 * resolves it against the product default instead.
 */
/** A Visual guide version, within 0049's CHECK bounds. */
const guideVersionSchema = z.number().int().min(GUIDE_VERSION_MIN).max(GUIDE_VERSION_MAX);
export const adviserPreferencesSchema = z
    .object({
    // Required-and-nullable in the stored/returned shape: a response always
    // states every setting, so a reader never has to guess whether an absent
    // key means "default" or "this server predates the field".
    builderLayout: builderLayoutSchema.nullable(),
    uiTheme: uiThemeSchema.nullable(),
    chartColourScheme: chartColourSchemeSchema.nullable(),
    workspaceMode: workspaceModeSchema.nullable(),
    factFindNavigation: factFindNavigationSchema.nullable(),
    defaultChartMetric: defaultChartMetricSchema.nullable(),
    chartEventDisplay: chartEventDisplaySchema.nullable(),
    showAssetCharts: z.boolean().nullable(),
    showResultsShortcut: z.boolean().nullable(),
    // Workspace v2 §14 / §15.7 (0049): the GUIDE VERSION this adviser dismissed
    // the Clients first-use strip at, completed the Visual guide at, or closed
    // it at. Null is "never". Not a setting the adviser picks — progress, kept
    // beside the settings because it has exactly their grain and boundary.
    exampleStripDismissedVersion: guideVersionSchema.nullable(),
    visualGuideCompletedVersion: guideVersionSchema.nullable(),
    visualGuideDismissedVersion: guideVersionSchema.nullable(),
})
    .strict();
/** Every preference key, in contract order. The route's column map and the web cache are built from this. */
export const ADVISER_PREFERENCE_KEYS = Object.freeze(Object.keys(adviserPreferencesSchema.shape));
/** "Never chose anything" — every setting inherits its product default. */
export const EMPTY_ADVISER_PREFERENCES = Object.freeze({
    builderLayout: null,
    uiTheme: null,
    chartColourScheme: null,
    workspaceMode: null,
    factFindNavigation: null,
    defaultChartMetric: null,
    chartEventDisplay: null,
    showAssetCharts: null,
    showResultsShortcut: null,
    exampleStripDismissedVersion: null,
    visualGuideCompletedVersion: null,
    visualGuideDismissedVersion: null,
});
/**
 * Resolve an adviser's preferences against the product defaults. Pure; total.
 *
 * `??` and never `||`: an explicit `false` for a switch is a choice, not an
 * absence. Accepts a partial object so a two-field value from an older server
 * or cache resolves rather than throwing.
 */
export function resolveAdviserPreferences(preferences) {
    return {
        builderLayout: preferences?.builderLayout ?? PRODUCT_DEFAULT_BUILDER_LAYOUT,
        uiTheme: preferences?.uiTheme ?? PRODUCT_DEFAULT_UI_THEME,
        chartColourScheme: preferences?.chartColourScheme ?? PRODUCT_DEFAULT_CHART_COLOUR_SCHEME,
        workspaceMode: preferences?.workspaceMode ?? PRODUCT_DEFAULT_WORKSPACE_MODE,
        factFindNavigation: preferences?.factFindNavigation ?? PRODUCT_DEFAULT_FACT_FIND_NAVIGATION,
        defaultChartMetric: preferences?.defaultChartMetric ?? PRODUCT_DEFAULT_CHART_METRIC,
        chartEventDisplay: preferences?.chartEventDisplay ?? PRODUCT_DEFAULT_CHART_EVENT_DISPLAY,
        showAssetCharts: preferences?.showAssetCharts ?? PRODUCT_DEFAULT_SHOW_ASSET_CHARTS,
        showResultsShortcut: preferences?.showResultsShortcut ?? PRODUCT_DEFAULT_SHOW_RESULTS_SHORTCUT,
    };
}
/**
 * PUT body. A MERGE, not a replace (spec v2 §7.4: "a partial write must not
 * erase them"):
 *   - a key that is ABSENT keeps its stored value;
 *   - a key that is `null` clears that one setting back to its default;
 *   - an empty body states nothing to change and is refused.
 * Still `.strict()`, so an `adviserId` / `firmId` in the body is a 422 — the
 * route has no addressable way to name another adviser.
 *
 * The merge is also what makes a rollout safe: a browser still running an
 * older bundle that knows two fields cannot wipe the six it has never heard of.
 */
export const adviserPreferencesUpdateSchema = adviserPreferencesSchema
    .partial()
    .strict()
    .refine((body) => Object.keys(body).length > 0, {
    message: 'State at least one setting to change.',
});
/** GET/PUT response payload. The API validates strictly: every key, nothing extra. */
export const adviserPreferencesResponseSchema = z
    .object({ preferences: adviserPreferencesSchema })
    .strict();
/**
 * The WEB's reading of a preferences payload. Deliberately more tolerant than
 * the API's response check, because web and API roll out one after the other
 * and browser tabs keep an old bundle open for hours:
 *   - an unknown key (a newer server) is ignored;
 *   - a missing key (an older server) reads as null, i.e. the default;
 *   - a key that is present but INVALID makes the whole value unreadable
 *     (`null`), so the caller keeps its last known good settings instead of
 *     half-applying a corrupt one.
 */
export function parseAdviserPreferencesLenient(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return null;
    const source = value;
    const filled = Object.fromEntries(ADVISER_PREFERENCE_KEYS.map((key) => [key, source[key] ?? null]));
    const parsed = adviserPreferencesSchema.safeParse(filled);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=schemas.js.map